import { useCallback, useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import {
  ApiError,
  createOneTimeCoachSession,
  listTables,
  listUsers,
  slotConflictDetail,
} from '../../lib/api';
import { Button } from '../../components/Button';
import { DatePicker } from '../../components/DatePicker';
import { Field } from '../../components/Field';
import { SlotConflictBanner } from '../../components/SlotConflictBanner';
import { SlotWindowFields } from '../../components/SlotWindowFields';
import { useToast } from '../../hooks/useToast';
import { slotWindowError } from '../../lib/coach-session-window';
import { fullName } from '../../lib/format';
import { PS, T } from '../../lib/theme';
import { formatClock, todayLocal } from '../../lib/time';
import type { AdminLookupUser, Paged, SlotConflictDetail, TableEmbed } from '../../lib/types';

/**
 * Create a single off-cycle coach session — trial lesson, make-up, substitute
 * slot (`POST /admin/coach-series/one-time`, givip/prospin-api#83).
 *
 * The API stores it as a coach series of length 1 so it keeps full coach
 * semantics (`coach_block`, coach attribution, the wider door window, the kiosk
 * badge) and is cancelled through the same route as any series. That is why
 * success lands on the series detail page, which labels it one-time.
 *
 * Window rules are shared with the recurring form through `SlotWindowFields` /
 * `slotWindowError`; the minimum-duration threshold stays the API's to state
 * (see .ai/features/47-one-time-coach-booking/DECISIONS.md).
 */
export function NewOneTimeCoachSessionPage() {
  const navigate = useNavigate();
  const { push } = useToast();

  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);
  const tables = useApi<TableEmbed[]>(tablesLoader);

  // Coaches are a small list (typically < 20); fetch up to 200 users and filter
  // client-side — same approach as the new-series form and the coaches list.
  const coachesLoader = useCallback(
    (signal: AbortSignal) => listUsers({ limit: 200 }, signal),
    [],
  );
  const usersPage = useApi<Paged<AdminLookupUser>>(coachesLoader);
  const coaches = (usersPage.data?.items ?? []).filter((u) => u.is_coach);

  const [coachId, setCoachId] = useState('');
  const [tableId, setTableId] = useState('');
  const [date, setDate] = useState(todayLocal());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState<SlotConflictDetail | null>(null);

  const coach = coaches.find((c) => c.id === coachId) ?? null;
  const windowError = slotWindowError(startTime, endTime);

  const canSubmit =
    coach !== null &&
    tableId !== '' &&
    date !== '' &&
    startTime !== '' &&
    endTime !== '' &&
    windowError === null &&
    !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !coach) return;
    setSubmitting(true);
    setConflict(null);
    try {
      const res = await createOneTimeCoachSession({
        coach_user_id: coach.id,
        table_id: Number(tableId),
        date,
        start_time: startTime,
        end_time: endTime,
      });
      push(
        'success',
        `One-time session #${res.series.id} created — ${res.booking.date} ` +
          `${formatClock(res.booking.start_time)}–${formatClock(res.booking.end_time)}.`,
      );
      navigate(`/coaches/series/${res.series.id}`);
    } catch (err) {
      // The structured 409 goes to the banner, where staff can read who holds
      // the slot; everything else (400 slot shape, 400 duration, 400 not-a-coach)
      // is a sentence the API authored, so pass it through verbatim.
      const detail = slotConflictDetail(err);
      if (detail) {
        setConflict(detail);
        push('error', detail.error);
        return;
      }
      push('error', err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>New one-time coach session</h1>
        <p style={{ ...T.caption, color: PS.textSec, marginTop: 4 }}>
          A single off-cycle session — trial lesson, make-up, substitute slot. Booked as a coach
          block on one date, with no weekly recurrence.
        </p>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          background: PS.white,
          padding: 20,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
        }}
      >
        <Field label="Coach">
          <select value={coachId} onChange={(e) => setCoachId(e.target.value)} style={selectStyle}>
            <option value="">
              {usersPage.loading
                ? 'Loading coaches…'
                : coaches.length === 0
                  ? 'No coaches — promote one in /coaches first'
                  : '— pick a coach —'}
            </option>
            {coaches.map((c) => (
              <option key={c.id} value={c.id}>
                {fullName(c)}
                {c.phone ? ` · ${c.phone}` : ''}
              </option>
            ))}
          </select>
        </Field>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Table">
            <select value={tableId} onChange={(e) => setTableId(e.target.value)} style={selectStyle}>
              <option value="">—</option>
              {(tables.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Date">
            {/* The API refuses a past date outright (it would reserve a table in
                history that `end_series` could never release), so the picker
                does not offer one. The API still enforces it. */}
            <DatePicker value={date} onChange={setDate} min={todayLocal()} />
          </Field>
        </div>

        <SlotWindowFields
          startTime={startTime}
          endTime={endTime}
          onStartTimeChange={setStartTime}
          onEndTimeChange={setEndTime}
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create one-time session'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/coaches')}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </form>

      {conflict && <SlotConflictBanner detail={conflict} />}
    </div>
  );
}

const selectStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  background: PS.white,
};
